import moment from "moment";
import { RowData } from "../common/GridWrapper/DataTable";
import AppConstants from "../constants";
import { enumerateDaysBetweenDates, fixDecimals, getUniqueValues, localTimeString } from "./helpers.utils";
import { getFilterAttributeValue } from "../mocks/SharedTransform/response.transforms";
import DeliveryConstants from "constants/SharedConstants/DeliveryConstants";

// Common interfaces
export interface MetaData {
  paymentType: string;
  totalAmount: string;
  transactionDate: Date;
  amountToCollect: string;
  collectedAmount: string;
  currency: string;
  category: string;
  deliveryType: string;
  modeOfCollection: string;
  invoiceNumber: string;
  internalExternal: string;
  origin: string;
  status: string;
  deliveryStatus: string;
  routeId: string;
  routeName: string;
  routeStatus: string;
  numberOfItems: number;
  lastStatusUpdatedTime: string;
}

export interface Name {
  ar: string;
  en: string;
}

export interface Product {
  sku: string;
  entryNumber: string;
  hasRejectedProducts: boolean;
  name: Name;
  quantity: string;
  acceptedQty: string;
  unitPrice: string;
  imgUrl: string;
  vatPercentage: string;
  uom: string;
  orderedBarcode: string;
  associatedBarcodes: string[];
  substitution: boolean;
}

export interface Address {
  area: string;
  landmark: string;
  building: string;
  apartment: string;
  zoneNumber: string;
  makani: string;
  streetNumber: string;
  streetName: string;
  city: string;
  postalCode: string;
  countryName: string;
  countryIsoCode: string;
  longitude: string;
  latitude: string;
}

export interface ContactInfo {
  name: string;
  phone: string;
  altphone: string;
  email: string;
  address: Address;
}

export interface PosInformation {
  store: string;
  storeName: string;
  contactInfo: ContactInfo;
}

export interface DeliverySlot {
  startTime: Date;
  endTime: Date;
}

export interface CustomerContactInfo {
  name: string;
  phone: string;
  email: string;
  address: Address;
}

export interface DeliveryInformation {
  deliveryInstructions: string;
  deliveryCost: string;
  carrier: string;
  carrierTrackingId: string;
  shipmentSize: string;
  totalCratesCount: number;
  deliverySlot: DeliverySlot;
  customerContactInfo: CustomerContactInfo;
  driver: Driver;
  helper: Driver;
  vehicle: Vehicle;
  transitTime: string;
  actualTransitTime: string;
  dynamicTransitTime: string;
  estimatedTransitTime: string;
}

export interface PickUpContactInfo {
  companyName: string;
  name: string;
  phone: string;
  email: string;
  address: Address;
}

export interface PickupSlot {
  startTime: Date;
  endTime: Date;
}

export interface PickUpInformation {
  pickUpContactInfo: PickUpContactInfo;
  pickupSlot: PickupSlot;
}

export interface Reason {
  deliveryStatus: string;
  reasonCode: string;
  reasonDescription: string;
}

export interface Order {
  id: string;
  orderCode: string;
  consignmentCode: string;
  hasRejectedProducts: boolean;
  retryCount: number;
  metadata: MetaData;
  products: Product[];
  reason: Reason;
  posInformation: PosInformation;
  deliveryInformation: DeliveryInformation;
  pickUpInformation: PickUpInformation;
  statuses: Statuses[];
}

export interface CountryDetails {
  countryCode: string;
  countryName: string;
}

export interface AssignedStatus {
  vehicleAssigned: boolean;
  helperAssigned: boolean;
  driverAssigned: boolean;
  consignmentsAvailable: boolean;
}

export interface Vehicle {
  brand: string;
  country: string;
  crateCapacity: number;
  fleetId: string;
  id: string;
  orderCapacity: number;
  type: string;
  name: string;
}

export interface Driver {
  countryIsoCode: string;
  countryName: string;
  licenseNumber: number;
  id: string;
  name: string;
  type: string;
}

export interface Route {
  id: string;
  routeId: string;
  creationTime: string;
  plannedDate: string;
  createdBy: string;
  carrier: string;
  deliveryProposition: string;
  deliveryArea: string[];
  deliverySlot: string[];
  countryDetails: CountryDetails;
  totalConsignment: number;
  totalCrates: number;
  transitTime: number;
  displayRouteStatus: string;
  hubCode: string;
  hubName: string;
  status: string;
  assignedStatus: AssignedStatus;
  assignedVehicle: Vehicle;
  assignee: Driver;
  helper: Driver;
  actualTransitTime: string;
}

export interface Sort {
  sorted: boolean;
  unsorted: boolean;
  empty: boolean;
}

export interface Pageable {
  sort: Sort;
  offset: number;
  requestContinuation: string;
  pageNumber: number;
  pageSize: number;
  paged: boolean;
  unpaged: boolean;
}

export interface Sort2 {
  sorted: boolean;
  unsorted: boolean;
  empty: boolean;
}

export interface OrdersDataResponse {
  pageNumber: number;
  totalElements: number;
  totalPages: number;
  continuationToken?: string;
  elements: Order[];
}

export interface RoutesDataResponse {
  pageNumber: number;
  totalElements: number;
  totalPages: number;
  continuationToken?: string;
  elements: Route[];
}

export interface Statuses {
  statusCode: string;
  statusName: string;
  statusDate: string;
  reason: Reason;
}

export interface SubsProducts {
  sku: string;
  name: Name;
  quantity: string;
  shippedQty: string;
  rejectedQty: string;
  unitPrice: string;
  vatPercentage: string;
  uom: string;
  reason: Reason;
}

export interface ProductsDataTransform {
  sku: string;
  name: Name;
  quantity: string;
  shippedQty: string;
  rejectedQty: string;
  unitPrice: string;
  vatPercentage: string;
  uom: string;
  reason: Reason;
  substitution: boolean;
  substitutedProducts: SubsProducts[];
}

export interface Products {
  consignmentCode: string;
  products: ProductsDataTransform[];
}

export const getAddressInfo = (addressObj: Address) => {
  return addressObj
    ? `${addressObj.building ? addressObj.building + ", " : ""}${
        addressObj.apartment ? addressObj.apartment + ", " : ""
      }${addressObj.zoneNumber ? addressObj.zoneNumber + ", " : ""}${
        addressObj.makani ? addressObj.makani + ", " : ""
      }${addressObj.streetNumber ? addressObj.streetNumber + ", " : ""}${
        addressObj.streetName ? addressObj.streetName + ", " : ""
      }${addressObj.area ? addressObj.area + ", " : ""}${
        addressObj.zoneNumber ? addressObj.zoneNumber : ""
      }
  ${addressObj.city ? addressObj.city + ", " : ""}${
        addressObj.postalCode ? addressObj.postalCode + ", " : ""
      }${addressObj.countryIsoCode ? addressObj.countryIsoCode : ""}`
    : "";
};

export const getLatLong = (addressObj: Address) => {
  return addressObj
    ? `${
        addressObj.latitude
          ? parseFloat(addressObj.latitude).toFixed(5) + ", "
          : ""
      }${
        addressObj.longitude ? parseFloat(addressObj.longitude).toFixed(5) : ""
      }`
    : "";
};

export const createPublishRoutesPayload = (routes: RowData[]) => {
  return routes.length
    ? {
        countryCode: sessionStorage.getItem(AppConstants.COUNTRY_CODE),
        ids: routes.map((route: RowData) => route.id),
      }
    : null;
};

export const createDeleteRoutePayload = (id: string) => {
  return id
    ? {
        countryCode: sessionStorage.getItem(AppConstants.COUNTRY_CODE),
        id: id,
      }
    : null;
};

export const createUpdateOrderStatusPayload = (params: any) => {
  return {
    countryCode: sessionStorage.getItem(AppConstants.COUNTRY_CODE),
    consignmentIds: params.consignments,
    displayRouteStatus: params.status,
    routeId: params.routeId,
  };
};

export const getDiscardRoutePayload = (params: any) => {
  const routeId = sessionStorage.getItem("routeId");
  const consignments = JSON.parse(
    sessionStorage.getItem("extraConsignments") || "[]"
  );
  const previousConsignments = params.previousCons;
  const responsePayload: any = {
    countryCode: sessionStorage.getItem(AppConstants.COUNTRY_CODE),
    routeId: routeId || "",
    previousConsignments: previousConsignments,
  };
  if (consignments.length) {
    responsePayload.consignmentsAdded = consignments;
  }
  return responsePayload;
};

export const createPayloadForUpdatingCustAddress = (
  params: any,
  consignmentId: any
) => {
  return {
    consignmentCode: consignmentId,
    address: params,
  };
};

export const getPlannedRoutesData = (routeData: any, includesCrates = true) => {
  const plannedSlots: any[] = [];
  if (routeData.PLANNED) {
    const plannedValue: any = routeData.PLANNED;
    for (const slotKey in plannedValue) {
      const slotValue: any = plannedValue[slotKey];
      const slot: any = {
        code: slotKey,
        value: slotValue.slotValue,
        data: [],
      };
      let totalConsignments = 0;
      let totalCrates = 0;
      const elements: any = slotValue.elements.map((route: any) => {
        totalConsignments += route.totalConsignment;
        if (includesCrates) {
          totalCrates += route.totalCrates;
        }
        return {
          code: route.id,
          value: route.routeId,
          data: route,
          slot: slotKey,
          slotValue: slotValue.slotValue,
        };
      });
      slot.data = elements;
      slot.totalConsignments = totalConsignments;
      if (includesCrates) {
        slot.totalCrates = totalCrates;
      }
      plannedSlots.push(slot);
    }
  }
  return plannedSlots;
};

export const transformRouteConsignments = (consignments: any[]) => {
  const routeConsignments: any = {};
  const seqConsignments: any = [];
  consignments.forEach((consignment: any) => {
    const newConsignment: any = {
      code: consignment.id,
      value: consignment.id,
      data: consignment,
      area:
        consignment.deliveryInformation &&
        consignment.deliveryInformation.customerContactInfo &&
        consignment.deliveryInformation.customerContactInfo.address.area,
      slot:
        consignment.deliveryInformation &&
        consignment.deliveryInformation.deliverySlot.deliverySlotTime,
    };
    seqConsignments.push(consignment.id);
    if (routeConsignments[newConsignment.area]) {
      if (routeConsignments[newConsignment.area][newConsignment.slot]) {
        routeConsignments[newConsignment.area][newConsignment.slot].push(
          newConsignment
        );
      } else {
        routeConsignments[newConsignment.area][newConsignment.slot] = [
          newConsignment,
        ];
      }
    } else {
      routeConsignments[newConsignment.area] = {
        [newConsignment.slot]: [newConsignment],
      };
    }
  });
  return {
    routeConsignments: routeConsignments,
    seqConsignments: seqConsignments,
  };
};

export const getAllAreasAndSlots = (areas: any[]) => {
  const allAreas: string[] = [];
  areas.forEach((area: any) => {
    const slots = area.data;
    slots.forEach((slot: any) => {
      allAreas.push(`${area.code}-${slot.code}`);
    });
    allAreas.push(area.code);
  });
  return allAreas;
};

export const getDropdownsData = (
  payload: any,
  codeKey?: string,
  valueKey?: string
) => {
  return payload.length
    ? payload.map((item: any) => {
        return {
          code: codeKey ? item[codeKey] : item.id,
          value: valueKey ? item[valueKey] : item.name,
        };
      })
    : [];
};

// Common createFilterPayload core functionality
export const createBaseFilterPayload = (
  payload: any,
  data?: any,
  isLoadMore?: boolean
) => {
  payload.attributes = [];

  // Process common fields
  const commonFields = [
    "hub",
    "hubName",
    "deliveryArea",
    "orderType",
    "deliveryStatus",
    "paymentType",
    "carrier",
    "routeId",
    "routeStatus",
    "driver",
    "vehicle",
    "helper",
    "deliveryFailedReason",
    "createdBy",
    "displayRouteStatus",
  ];

  commonFields.forEach((field) => {
    if (payload[field]) {
      if (field === "hub" && payload[field].value) {
        let hubValue = payload[field].value.map((item: any) => item.value);
        let hubNameValue = [];
        if (payload.hubName && payload.hubName.value.length) {
          hubNameValue = payload.hubName.value.map((item: any) => item.value);
        }
        hubValue = hubValue.concat(hubNameValue);
        const selectAllIdx = hubValue.indexOf(AppConstants.SELECT_ALL.value);
        const selectNoneIdx = hubValue.indexOf(AppConstants.SELECT_NONE.value);
        if (selectAllIdx > -1) {
          hubValue = [];
        }
        if (selectNoneIdx > -1) {
          hubValue = [];
        }
        payload.attributes.push({
          key: payload[field].fieldName,
          value: getUniqueValues(hubValue),
        });
        delete payload.hub;
        delete payload.hubName;
      } else if (field === "hub") {
        payload.attributes.push({
          key: payload[field].fieldName,
          value: [],
        });
        delete payload.hub;
        delete payload.hubName;
      } else if (field === "displayRouteStatus") {
        payload.attributes.push({
          key: payload[field].fieldName,
          value: payload[field].value || [],
        });
        delete payload[field];
      } else if (field !== "hub" && field !== "hubName" && payload[field]) {
        let attrValue = [];
        if (payload[field].value) {
          attrValue = getFilterAttributeValue(payload[field].value);
        } else {
          attrValue = [];
        }
        payload.attributes.push({
          key: payload[field].fieldName,
          value: attrValue,
        });
        delete payload[field];
      }
    }
  });

  // Handle deliverySlot and dateRange
  if (payload.deliverySlot && payload.deliverySlot.value) {
    const delSlotValue = payload.deliverySlot.value.map(
      (item: any) => item.value
    );
    const dateRange: { startTime: string; endTime: string }[] = [];
    const selectAllIdx = delSlotValue.indexOf(AppConstants.SELECT_ALL.value);
    if (payload.dateRange && payload.dateRange.value) {
      const allDates = enumerateDaysBetweenDates(
        payload.dateRange.value.startDate,
        payload.dateRange.value.endDate
      );
      if (selectAllIdx > -1 || !delSlotValue.length) {
        payload.isDateRange = true;
        dateRange.push({
          startTime: moment(
            payload.dateRange.value.startDate,
            AppConstants.DATE_FORMAT
          )
            .hours(0)
            .minutes(0)
            .seconds(0)
            .format(AppConstants.DATE_FORMAT_BACKEND),
          endTime: moment(
            payload.dateRange.value.endDate,
            AppConstants.DATE_FORMAT
          )
            .hours(23)
            .minutes(59)
            .seconds(59)
            .format(AppConstants.DATE_FORMAT_BACKEND),
        });
      } else {
        payload.isDateRange = false;
        delSlotValue.forEach((slot: any) => {
          const splitSlot = slot.split("-");
          const startSlot = splitSlot[0].split(":");
          const endSlot = splitSlot[1].split(":");
          if (allDates.length) {
            allDates.forEach((date: string) => {
              dateRange.push({
                startTime: moment(date, AppConstants.DATE_FORMAT)
                  .hours(startSlot[0])
                  .minutes(startSlot[1])
                  .seconds(startSlot[2])
                  .format(AppConstants.DATE_FORMAT_BACKEND),
                endTime: moment(date, AppConstants.DATE_FORMAT)
                  .hours(endSlot[0])
                  .minutes(endSlot[1])
                  .seconds(endSlot[2])
                  .format(AppConstants.DATE_FORMAT_BACKEND),
              });
            });
          }
        });
      }
      if (payload.reset) {
        delete payload.isDateRange;
      }
    }
    payload.dateRange = dateRange;
    delete payload.displayRouteStatus;
    delete payload.deliverySlot;
    delete payload.reset;
  }

  // Handle pagination
  if (isLoadMore && data) {
    payload.pageNumber =
      data.pageNumber + 1 < data.totalPages
        ? data.pageNumber + 1
        : payload.pageNumber;
  }

  if (data && data.continuationToken && payload.pageNumber) {
    payload.requestContinuationToken = data.continuationToken;
  }

  return payload;
};

export const checkTerminalStatus = (deliveryStatus: string) => {
  return (
    DeliveryConstants.CONSIGNMENTS_DATA.TERMINAL_STATUSES.indexOf(
      deliveryStatus
    ) > -1
  );
};

export const getConsignmentStatusField = (
  fieldName: string,
  statusCode: string
) => {
  const statuses: any = DeliveryConstants.CONSIGNMENTS_DATA.STATUSES;
  return statusCode && statuses[statusCode]
    ? statuses[statusCode][fieldName]
    : "";
};

export const getRouteStatusField = (fieldName: string, statusCode: string) => {
  const statuses: any = DeliveryConstants.ROUTES_DATA.STATUSES;
  return statusCode && statuses[statusCode]
    ? statuses[statusCode][fieldName]
    : "";
};

export const CarrefourOrdersDataTransform = (
  response: OrdersDataResponse
): any[] => {
  const transformedArray: any[] = [];
  response.elements.forEach((order: Order) => {
    const {
      id,
      metadata,
      posInformation: { store, storeName },
      deliveryInformation,
      orderCode,
      products,
      reason,
      consignmentCode,
      statuses,
    } = order;
    // TODO: map/add actual objects too for drilling down content or store in state
    const deliveryDate = moment(
      deliveryInformation.deliverySlot.startTime,
      AppConstants.DATE_FORMAT_BACKEND
    );
    const startTime = new Date(deliveryInformation.deliverySlot.startTime);
    const endTime = new Date(deliveryInformation.deliverySlot.endTime);
    const customerContactInfo = deliveryInformation.customerContactInfo || {
      address: {},
    };
    const transactionDate = moment(
      metadata.transactionDate,
      AppConstants.DATE_FORMAT_BACKEND
    );
    const statusDate = metadata.lastStatusUpdatedTime
      ? moment(
          metadata.lastStatusUpdatedTime,
          AppConstants.DATE_FORMAT_BACKEND
        ).format(AppConstants.TIMESTAMP)
      : statuses && statuses.length
      ? moment(statuses[0].statusDate, AppConstants.DATE_FORMAT_BACKEND).format(
          AppConstants.TIMESTAMP
        )
      : "";
    transformedArray.push({
      id: id,
      hubCode: store,
      hubName: storeName,
      deliveryArea: customerContactInfo.address
        ? customerContactInfo.address.area
        : "",
      consignmentId: consignmentCode,
      type: metadata.category,
      deliveryDate: deliveryDate.format(AppConstants.DATE_FORMAT),
      startEndTime: `${localTimeString(startTime)} - ${localTimeString(
        endTime
      )}`,
      deliveryStatus: metadata.deliveryStatus,
      statusDate: statusDate,
      products: products && products.length ? products.length : 0,
      numberOfItems:
        metadata.numberOfItems && metadata.numberOfItems > 0
          ? metadata.numberOfItems
          : products && products.length
          ? products.length
          : 0,
      crates: deliveryInformation.totalCratesCount,
      orderId: orderCode,
      transactionDate: transactionDate.format(AppConstants.DATE_FORMAT),
      customerName: customerContactInfo.name,
      customerEmail: customerContactInfo.email,
      customerMobileNumber: customerContactInfo.phone,
      deliveryStreetAddress: getAddressInfo(customerContactInfo.address),
      landmark: customerContactInfo.address.landmark,
      paymentType: metadata.paymentType,
      totalAmount: fixDecimals(
        2,
        metadata.totalAmount ? metadata.totalAmount.toString() : ""
      ),
      codAmount: fixDecimals(
        2,
        metadata.amountToCollect ? metadata.amountToCollect.toString() : ""
      ),
      collectedAmount: metadata.collectedAmount
        ? parseFloat(metadata.collectedAmount.toString()).toFixed(2)
        : "0.00",
      carrier: deliveryInformation.carrier,
      deliveryProposition: metadata.deliveryType,
      routeId: metadata.routeName,
      routeStatus: metadata.routeStatus,
      driver: deliveryInformation.driver ? deliveryInformation.driver.name : "",
      helper: deliveryInformation.helper ? deliveryInformation.helper.name : "",
      vehicle: deliveryInformation.vehicle
        ? deliveryInformation.vehicle.name
        : "",
      plannedDuration: deliveryInformation.dynamicTransitTime
        ? deliveryInformation.dynamicTransitTime
        : deliveryInformation.estimatedTransitTime,
      actualDuration: deliveryInformation.actualTransitTime
        ? deliveryInformation.actualTransitTime
        : "",
      deliveryFailedReason: reason
        ? reason.reasonDescription
        : statuses && statuses.length && statuses[0].reason
        ? statuses[0].reason.reasonDescription
        : "",
    });
  });
  return [...transformedArray];
};

export const DeliveryRoutesDataTransform = (
  response: RoutesDataResponse
): any[] => {
  const transformedArray: any[] = [];
  response.elements.forEach((order: Route) => {
    const {
      id,
      routeId,
      hubCode,
      hubName,
      carrier,
      deliveryProposition,
      transitTime,
      deliveryArea,
      deliverySlot,
      plannedDate,
      status,
      totalConsignment,
      assignedVehicle,
      assignee,
      helper,
      createdBy,
      totalCrates,
      actualTransitTime,
    } = order;
    // TODO: map/add actual objects too for drilling down content or store in state
    const deliveryDate = moment(plannedDate, AppConstants.DATE_FORMAT_BACKEND);
    transformedArray.push({
      id: id,
      hubCode: hubCode,
      hubName: hubName,
      deliveryArea: deliveryArea && deliveryArea.length ? deliveryArea : [],
      deliveryDate: deliveryDate.format(AppConstants.DATE_FORMAT),
      deliverySlot: deliverySlot && deliverySlot.length ? deliverySlot : [],
      consignments: totalConsignment,
      crates: totalCrates,
      carrier: carrier,
      deliveryProposition: deliveryProposition,
      routeId: routeId,
      routeStatus: status,
      driver: assignee ? assignee.name : "",
      vehicle: assignedVehicle ? assignedVehicle.name : "",
      helper: helper ? helper.name : "",
      createdBy: createdBy,
      plannedDuration: transitTime,
      actualDuration: actualTransitTime,
      actions: order,
    });
  });
  return [...transformedArray];
};
